import {useState,type ReactNode} from "react";
import {useQuery,useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router";
import {useAuth} from "@/contexts/AuthContext";
import {Bento} from "@/components/ui/box";
import {ApiError} from "@/lib/api/client";
import {announcementsApi,type AnnouncementScope} from "@/lib/api/announcements";
import {useAnnouncementBox} from "@/components/inbox/use-announcement-box";
import {noticesBox} from "@/components/inbox/notices-box";
import {useReadMarks} from "@/components/inbox/use-read-marks";
import {InboxRefusal} from "@/components/inbox/inbox-refusal";
import {inboxRead} from "@/components/inbox/inbox-read";
import {inboxWords as words} from "@/components/inbox/inbox-copy";

export interface AnnouncementsPageProps {
  workspace?:"role"|"teaching"|"learning";scope?:AnnouncementScope;presentation?:"announcements"|"inbox";
  eyebrow?:string;courseId?:string;embedded?:boolean;action?:ReactNode;compose?:boolean;
}
export function AnnouncementsPage(props:AnnouncementsPageProps) {
  const [params]=useSearchParams();
  const invalid=["course","notice","compose"].some(key=>params.getAll(key).length>1)
    ||Boolean(params.has("compose")&&params.get("compose")!=="notice")
    ||Boolean(params.has("notice")&&params.has("compose"))
    ||Boolean(props.courseId&&params.get("course")&&props.courseId!==params.get("course"));
  if(invalid)return <InboxRefusal page="notices"/>;
  return <NoticesWorkspace {...props}/>;
}
function NoticesWorkspace({workspace="role",scope,presentation="announcements",eyebrow,courseId,embedded=false,action,compose}:AnnouncementsPageProps){
  const {me,role:accountRole}=useAuth();
  const role=workspace==="teaching"?"instructor":workspace==="learning"?"student":accountRole;
  const canCompose=(compose??presentation==="announcements")&&(role==="admin"||role==="instructor");
  const [params,setParams]=useSearchParams();
  const course=courseId??params.get("course")??"";
  const selected=params.get("notice")??"";
  const ranking=useQuery({queryKey:["announcements",me?.tenant_id,me?.user_id,"ranking",workspace],queryFn:()=>announcementsApi.ranking(workspace==="teaching"),enabled:canCompose,retry:false});
  const announcement=useAnnouncementBox({course,pinned:Boolean(courseId),teaching:workspace==="teaching",enabled:canCompose});
  const [query,setQuery]=useState("");
  const client=useQueryClient();
  const options={...(scope?{scope}:{}),...(courseId?{course_id:courseId}:{}),
    ...(workspace==="teaching"?{teaching:true}:{}),...(workspace==="learning"?{learning:true}:{})};
  const read=useQuery({queryKey:["announcements",me?.tenant_id,me?.user_id,"feed",options],queryFn:()=>announcementsApi.list(options),retry:false});
  const data=read.data;
  const marks=useReadMarks({items:data?.items.map(item=>({id:item.id,unread:!item.is_read})),selected,
    write:announcementsApi.markRead,reread:()=>void client.invalidateQueries({queryKey:["announcements"]})});
  const exact=data?.items.find(item=>item.id===selected);
  if(selected&&(read.isSuccess&&!exact||exact&&course&&exact.course_id!==course
    ||read.error instanceof ApiError&&[403,404,422].includes(read.error.status)))return <InboxRefusal page="notices"/>;
  const change=(name:string,value:string)=>{
    const next=new URLSearchParams(params);
    if(value)next.set(name,value);else next.delete(name);
    setParams(next);
  };
  const list=noticesBox({read:inboxRead(read,words.noticesFailed),course,pinned:Boolean(courseId),onCourseChange:value=>change("course",value),selected,
    mark:marks.mark,query,onQueryChange:setQuery});
  const rank=!ranking.isError&&ranking.data?.state==="ready"?ranking.data.value?.candidates.find(item=>item.id==="new-announcement"):undefined;
  const composeBox=rank?{...announcement.box,candidate:{eligible:rank.eligible,rank:rank.rank,blocked:null}}:announcement.box;
  return <div className={embedded?"grid gap-5":"app-page"}>
    {!embedded&&<header data-slot="page-frame" className="flex flex-wrap items-end justify-between gap-4">
      <div>{eyebrow&&<p className="text-meta text-muted-foreground">{eyebrow}</p>}<h1 className="text-title font-semibold">{presentation==="inbox"?words.inbox:words.notices}</h1></div>{action}
    </header>}
    <Bento id="notices" journey="notices" boxes={canCompose?[composeBox,list]:[list]} defaultOpen={params.get("compose")==="notice"?["new-announcement"]:[]}
      onOpenChange={(id,open)=>{if(id==="new-announcement"&&open)announcement.reset();}}
      onRowOpenChange={(_,id,open)=>{
        change("notice",open?id:"");if(open)marks.open(id);else marks.close(id);
      }}/>
  </div>;
}
